import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { KeyRound, Lock, AlertCircle, CalendarDays, CheckSquare, ChevronRight, ClipboardCheck, FileText, GraduationCap, LifeBuoy, MessageSquare, QrCode, UserPlus, Users, Cake, Video } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/stores/auth';
import { arr } from '@/features/shared/ops/kit';

/** Front-office home: who is in today, what is waiting on the desk, and the day's events. */

function Tile({ to, label, value, hint, tone = 'text-slate-900' }: { to: string; label: string; value: ReactNode; hint?: ReactNode; tone?: string }) {
  return (
    <Link to={to} className="bg-white rounded-2xl border border-slate-200 p-3.5 hover:border-slate-300 block min-w-0">
      <p className="text-xs text-slate-500 truncate">{label}</p>
      <p className={`text-2xl font-extrabold mt-1 ${tone}`}>{value}</p>
      {hint && <p className="text-[11px] text-slate-500 truncate">{hint}</p>}
    </Link>
  );
}

function Panel({ title, to, link, children }: { title: string; to?: string; link?: string; children: ReactNode }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200">
      <div className="flex items-center justify-between px-4 pt-3.5 pb-2"><h2 className="font-bold text-slate-900">{title}</h2>{to && <Link to={to} className="text-xs font-semibold text-[#0A2540] inline-flex items-center">{link ?? 'Open'}<ChevronRight className="w-3.5 h-3.5" /></Link>}</div>
      <div className="px-4 pb-4">{children}</div>
    </section>
  );
}

export function DashboardPage() {
  const name = useAuth((s) => s.user?.name)?.split(' ')[0];
  const q = useQuery({ queryKey: ['owner-dashboard'], queryFn: () => api.get('/school/dashboard').then((r) => r.data?.data) });
  const d: any = q.data;
  const a = d?.attendance_today;
  const st = d?.staff_today;
  const p = d?.pending ?? {};
  const todo = [
    { n: p.applications, t: 'admission applications to review', to: '/desk/admissions', icon: UserPlus },
    { n: p.parent_link_requests, t: 'parents waiting to be linked to a child', to: '/desk/families', icon: Users },
    { n: p.document_requests, t: 'document / transcript requests', to: '/desk/graduation-alumni', icon: FileText },
    { n: p.held_results, t: 'results on hold', to: '/desk/held-results', icon: AlertCircle },
    { n: p.lesson_notes, t: 'lesson notes waiting for review', to: '/desk/lesson-notes', icon: ClipboardCheck },
    { n: p.support_replies, t: 'replies from OJP support', to: '/desk/support', icon: LifeBuoy },
  ].filter((x) => x.n > 0);
  const hour = new Date().getHours();

  return (
    <div className="space-y-4 max-w-6xl">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{[d?.session?.name, d?.term?.name].filter(Boolean).join(' · ') || new Date().toDateString()}</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Good {hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening'}{name ? `, ${name}` : ''}</h1>
      </div>
      {q.isError && <p className="text-sm text-red-600">Could not load the dashboard. Pull to refresh.</p>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile to="/desk/students" label="Active students" value={d?.counts?.students ?? '…'} hint={d?.counts ? `${d.counts.male ?? 0} boys · ${d.counts.female ?? 0} girls` : undefined} />
        <Tile to="/desk/attendance" label="Students present today" value={a ? (a.marked ? `${a.rate}%` : 'Not marked') : '…'} hint={a?.marked ? `${a.present + a.late} in · ${a.absent} absent` : 'Mark the register'} tone={a && a.marked && a.rate < 80 ? 'text-red-600' : 'text-slate-900'} />
        <Tile to="/desk/staff-attendance" label="Staff clocked in" value={st && st.total != null ? `${st.clocked_in ?? 0}/${st.total}` : '…'} hint={st?.late ? `${st.late} late` : undefined} />
        <Tile to="/desk/admissions" label="Pending applications" value={p.applications ?? '…'} tone={p.applications ? 'text-blue-700' : 'text-slate-900'} />
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {[['/desk/admissions', 'New application', FileText], ['/desk/students', 'Add student', UserPlus], ['/desk/attendance', 'Attendance', CheckSquare], ['/desk/logins', 'Reset a password', KeyRound], ['/desk/score-locks', 'Lock scores', Lock], ['/desk/notices', 'Send notice', MessageSquare]].map(([to, l, I]: any) => (
          <Link key={to} to={to} className="bg-white rounded-2xl border border-slate-200 py-3 flex flex-col items-center gap-1.5 text-xs font-semibold text-slate-700 hover:border-slate-300 text-center px-1"><I className="w-5 h-5 text-[#0A2540]" />{l}</Link>
        ))}
      </div>

      <Panel title="Waiting on the desk">
        {!todo.length ? <p className="text-sm text-slate-500">{q.isLoading ? 'Loading…' : 'Nothing waiting. Well done.'}</p> : (
          <ul className="divide-y divide-slate-100">
            {todo.map((x) => <li key={x.t}><Link to={x.to} className="py-2.5 flex items-center gap-3 text-sm"><x.icon className="w-4 h-4 text-amber-600 shrink-0" /><span className="flex-1"><b>{x.n}</b> {x.t}</span><ChevronRight className="w-4 h-4 text-slate-400" /></Link></li>)}
          </ul>
        )}
      </Panel>

      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="Coming up" to="/desk/calendar" link="Calendar">
          {!arr(d?.upcoming_events).length ? <p className="text-sm text-slate-500">Nothing on the calendar.</p> : (
            <ul className="divide-y divide-slate-100">{arr(d?.upcoming_events).map((e: any) => (
              <li key={e.id} className="py-2 flex items-center gap-3 text-sm"><CalendarDays className="w-4 h-4 text-slate-400 shrink-0" /><span className="flex-1 truncate font-medium">{e.title}</span><span className="text-xs text-slate-500">{new Date(e.starts_at).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })}</span></li>
            ))}</ul>
          )}
        </Panel>
        <Panel title="Today" to="/desk/birthdays" link="Birthdays">
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-3"><Cake className="w-4 h-4 text-pink-600" />{d?.birthdays_today ? `${d.birthdays_today} birthday${d.birthdays_today > 1 ? 's' : ''} today — send wishes` : 'No birthdays today'}</li>
            <li className="flex items-center gap-3"><Video className="w-4 h-4 text-[#0A2540]" />{d?.teaching ? `${d.teaching.live_today} live class${d.teaching.live_today === 1 ? '' : 'es'} today${d.teaching.live_now ? ` · ${d.teaching.live_now} live now` : ''}` : '…'}</li>
            <li className="flex items-center gap-3"><MessageSquare className="w-4 h-4 text-[#0A2540]" />{d?.sms_balance != null ? `SMS balance: ${d.sms_balance} units` : 'SMS balance is shown to the owner'}</li>
          </ul>
        </Panel>
      </div>
    </div>
  );
}
